import { DeviceStatusFlags, KioDeviceStatus, RawKioDeviceStatus } from '../MonitoredDevice';

export enum TransactionStates {
	NotInitialized = 0,
	Ready = 1,
	CardRequested = 2,
	AmountConfirmationStarted = 3,
	CardRemovalRequested = 4,
	PinEntryStarted = 5,
	TransactionStarted = 6,
	OnlineAuthRequested = 7,
	OnlineAuthCompleted = 8,
}

// must be stringified in the KioEMVDevice.Sale call
export type KioEMVTransactionParams = {
	DeviceName?: string;
	PaymentAmount: number; // in cents
	TaxAmount?: number; // in cents, only used for Shift4
	TransactionRef?: string;
	CardToken?: number;
	AuthCode?: string;
	AcqRefData?: string;
	ProcessData?: string;
	IsFullData?: string;
	TokenType?: number; // for grant street: 0 for credit, 1 for debit
	OrderID?: string;
	ProductDescriptors?: KioEMVProductDescriptor[]; // needed for grant street
};

export type KioEMVProductDescriptor = {
	SKU: string;
	Description: string;
	Quantity: number;
	TotalAmount: number;
	Department: string;
	Category: string;
	Type: string;
};

export type KioEMVResult = {
	TransType: number;
	Result: number;
	Amount: string;
	Tax: string;
	ReferenceID: string;
	AuthorizationCode: string;
	Errors: string;
	ErrorCode: string;
	ExpiryDate: string;
	PrimaryAccountNumber: string;
	CardHashToken: string;
	AcqRefData: string;
	ProcessData: string;
	ReceiptData: ReceiptLineItem[];
	CardReference: string;
	RawData: Record<string, unknown>;
	CardType: string;
	StoredCredentialToken?: string;
};

export type ReceiptLineItem = {
	// Define based on your receipt line item structure
	text: string;
	alignment?: string;
	LineNum: string;
	Data: string;
};

export type TransactionCompletedCallback = (result: KioEMVResult) => void;
export type TransactionStateChangedCallback = (newState: number) => void;
export type VP5300MsgCallback = (devName: string, msg: string, respObj: unknown) => void;

/** Parsed VP5300 token extraction result */
export interface VP5300TokenResult {
	cardToken: string;
	transactionID: string;
	paymentID: string;
	processorMsg: string;
}

export type BillingInfo = {
	name: string;
	email: string;
	phone: string;
	address: string;
	address2?: string;
	city: string;
	state: string;
	zip: string;
	country?: string;
};

export type CreateCartGSParams = {
	billingInfo: BillingInfo;
	orderId: string;
	productDescriptors?: KioEMVProductDescriptor[];
	devName?: string;
};

export type PayGoAccount = {
	firstName?: string;
	middleName?: string;
	lastName?: string;
	accountNumber?: string;
	active?: boolean;
	payType?: string;
	accountToken?: string;
	customerNumber?: string;
	CheckRestricted?: boolean;
	CreditRestricted?: boolean;
};

export default class EMVDevice {
	private deviceName?: string;
	private status: KioDeviceStatus = { statusBits: DeviceStatusFlags.Ok, details: {} };
	private onTransactionCompletedCallback?: TransactionCompletedCallback;
	private onStateChangedCallback?: TransactionStateChangedCallback;
	private onVP5300MsgCallback?: VP5300MsgCallback;

	constructor(deviceName?: string) {
		this.deviceName = deviceName;
		window.OnKioEMVTransactionCompleted = this.onTransactionCompleted.bind(this);
	}

	/**
	 * Register a callback for VP5300 device screen messages.
	 * Since the VP5300 is screenless, this receives the messages it would normally display.
	 */
	setOnVP5300Msg(callback: VP5300MsgCallback): void {
		this.onVP5300MsgCallback = callback;
		window.OnVP5300Msg = (devName: string, msg: string, respObj: unknown) => {
			this.onVP5300MsgCallback?.(devName, msg, respObj);
		};
	}

	/**
	 * Register a callback for EMV device state changes.
	 */
	setOnStateChanged(callback: TransactionStateChangedCallback): void {
		this.onStateChangedCallback = callback;
		window.OnKioEMVStateChanged = (newState: number) => {
			this.onStateChangedCallback?.(newState);
		};
	}

	getStatus(): KioDeviceStatus {
		if (!this.deviceName) return this.status;
		const rawStatus: RawKioDeviceStatus = JSON.parse(window.KioDevice.GetStatusEx(this.deviceName));
		return { statusBits: rawStatus.Status, details: rawStatus.Details };
	}

	createCartGS(params: CreateCartGSParams): boolean {
		return (
			window.KioEMVDevice.CreateCartGS(
				params.billingInfo,
				params.orderId.toString(),
				params.productDescriptors || [],
				params.devName || 'EMV'
			) || false
		);
	}

	payGoAccountLookup(accountNumber: string): PayGoAccount[] {
		return window.KioEMVDevice.PayGo_AccountLookup_Async(this.deviceName ?? null, null, null, null, null, accountNumber);
	}

	runSale(params: KioEMVTransactionParams, callback?: TransactionCompletedCallback): boolean {
		if (callback) {
			this.onTransactionCompletedCallback = callback;
		}
		try {
			return window.KioEMVDevice.Sale(JSON.stringify(params)) || false;
		} catch (err) {
			console.error('EMVDevice.runSale failed:', err);
			return false;
		}
	}

	cancelTransaction(): boolean {
		return window.KioEMVDevice.CancelTransaction(JSON.stringify({}));
	}

	/** Returns the current EMV device state code (e.g. 1 = Ready). */
	getState(): number {
		return window.KioEMVDevice.GetState();
	}

	/**
	 * Start a tokenization-only transaction (TokenType 5).
	 * Returns a CardSecure token without charging the card.
	 */
	runTokenization(params: Omit<KioEMVTransactionParams, 'TokenType'>, callback?: TransactionCompletedCallback): boolean {
		if (callback) {
			this.onTransactionCompletedCallback = callback;
		}
		try {
			return window.KioEMVDevice.Sale(JSON.stringify({ ...params, TokenType: 5 })) || false;
		} catch (err) {
			console.error('EMVDevice.runTokenization failed:', err);
			return false;
		}
	}

	/**
	 * Extract the CardSecure token from a VP5300 KioEMVResult.
	 * The token is nested: ReceiptData[FullResponseJson].Data → RawData → cardToken
	 */
	static extractVP5300Token(result: KioEMVResult): VP5300TokenResult | null {
		// First try the nested VP5300 path
		if (result.ReceiptData?.length) {
			const fullResponseEntry = result.ReceiptData.find((entry) => entry.LineNum === 'FullResponseJson');
			if (fullResponseEntry) {
				try {
					const fullResponse = JSON.parse(fullResponseEntry.Data);
					if (fullResponse?.RawData) {
						const txnData = JSON.parse(fullResponse.RawData);
						if (txnData?.cardToken) {
							return {
								cardToken: txnData.cardToken,
								transactionID: txnData.transactionID ?? '',
								paymentID: txnData.paymentID ?? '',
								processorMsg: txnData.processorMsg ?? '',
							};
						}
					}
				} catch {
					// Fall through to CardHashToken fallback
				}
			}
		}

		// Fallback to top-level CardHashToken
		if (result.CardHashToken) {
			return {
				cardToken: result.CardHashToken,
				transactionID: '',
				paymentID: '',
				processorMsg: '',
			};
		}

		return null;
	}

	private onTransactionCompleted(result: KioEMVResult): void {
		if (this.onTransactionCompletedCallback) {
			try {
				this.onTransactionCompletedCallback(result);
			} catch (error) {
				console.error('Failed to parse EMV transaction result:', error);
			}
		}
	}
}

export class EMVDeviceStub {
	getStatus(): KioDeviceStatus {
		return { statusBits: DeviceStatusFlags.Ok, details: {} };
	}
}
